import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import StatusBadge from "../components/app/StatusBadge";
import { Plus, MagnifyingGlass, FileText, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["tutti", "bozza", "inviato", "accettato", "firmato", "rifiutato"];

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("tutti");
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function remove(q, e) {
    e.stopPropagation();
    if (!window.confirm(`Eliminare preventivo "${q.numero}"?`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Preventivo eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  }

  const filtered = items.filter((q) => {
    const text = [q.numero, q.cliente_snapshot?.ragione_sociale].filter(Boolean).join(" ").toLowerCase();
    const matchSearch = text.includes(search.toLowerCase());
    const matchStatus = filter === "tutti" || q.stato === filter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Gestione</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Preventivi</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} preventivi totali</p>
        </div>
        <button
          onClick={() => navigate("/preventivi/nuovo")}
          data-testid="new-quote-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <Plus size={14} weight="bold" /> Nuovo Preventivo
        </button>
      </div>

      <div className="swiss-card">
        <div className="p-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              placeholder="Cerca per numero o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="quote-search-input"
              className="w-full bg-white border border-[#E2E8F0] rounded-sm pl-10 pr-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border transition-all ${
                  filter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0A0A0A]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={32} className="mx-auto text-[#64748B] mb-3" />
            <p className="text-sm text-[#64748B] mb-4">
              {items.length === 0 ? "Nessun preventivo creato." : "Nessun risultato."}
            </p>
            {items.length === 0 && (
              <button onClick={() => navigate("/preventivi/nuovo")} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline">
                Crea il primo preventivo
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Tabella desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7]">
                  <tr className="text-left">
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Numero</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Data</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Validità</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Totale</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/preventivi/${q.id}`)}
                      className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7] cursor-pointer"
                      data-testid={`quote-row-${q.id}`}
                    >
                      <td className="py-3 px-4 font-mono text-xs font-bold">{q.numero}</td>
                      <td className="py-3 px-4 font-semibold">{q.cliente_snapshot?.ragione_sociale}</td>
                      <td className="py-3 px-4 text-[#64748B]">{formatDate(q.data_emissione)}</td>
                      <td className="py-3 px-4 text-[#64748B]">{formatDate(q.data_validita)}</td>
                      <td className="py-3 px-4"><StatusBadge stato={q.stato} /></td>
                      <td className="py-3 px-4 text-right font-mono font-bold">{formatCurrency(q.totale)}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={(e) => remove(q, e)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-quote-${q.id}`}>
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card stack mobile */}
            <div className="md:hidden divide-y divide-[#E2E8F0]">
              {filtered.map((q) => (
                <div
                  key={q.id}
                  onClick={() => navigate(`/preventivi/${q.id}`)}
                  data-testid={`quote-card-${q.id}`}
                  className="px-4 py-4 active:bg-[#F4F5F7] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] font-bold text-[#64748B] uppercase">{q.numero}</div>
                      <div className="font-semibold text-sm truncate mt-0.5">{q.cliente_snapshot?.ragione_sociale}</div>
                    </div>
                    <StatusBadge stato={q.stato} />
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-[11px] text-[#64748B]">
                      {formatDate(q.data_emissione)}
                      {q.data_validita ? <> · <span className="text-[10px] uppercase tracking-[0.1em]">val.</span> {formatDate(q.data_validita)}</> : null}
                    </div>
                    <div className="font-mono font-bold text-sm">{formatCurrency(q.totale)}</div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={(e) => remove(q, e)}
                      className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 -mr-2"
                      data-testid={`delete-quote-mobile-${q.id}`}
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
